import React, { useState, useEffect } from 'react';
import { Box, Fab, Zoom } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Navbar from './components/Navbar';
import AboutUs from './components/AboutUs';
import Hero from './components/Hero';
import Services from './components/Services';
import PatientInfo from './components/PatientInfo';
import ContactUs from './components/ContactUs';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';
import physioTheme from '../../theme2';
import Seo from '../../components/seo/Seo';
import StructuredData from '../../components/seo/StructuredData';
import BreadcrumbsNav from '../../components/BreadcrumbsNav';
import {
  siteUrl,
  logoUrl,
  phone,
  address,
  openingHoursSpecification,
  sameAs,
} from '../../components/seo/config';

const PhysiotherapyPage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const physioSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: "Drax Hall Physiotherapy & Rehab",
    url: `${siteUrl}/physiotherapy`,
    logo: logoUrl,
    image: logoUrl,
    telephone: phone,
    address,
    openingHoursSpecification,
    sameAs,
    medicalSpecialty: ["Physiotherapy", "Rehabilitation", "SportsMedicine"],
    areaServed: "JM",
  };
  const physioFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Do I need a referral to see a physiotherapist?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, a referral is required for all new patients. Each referral is valid for up to six (6) months.",
        },
      },
      {
        "@type": "Question",
        name: "How long is a typical session?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Sessions typically last between 60 to 90 minutes, depending on the treatment plan.",
        },
      },
      {
        "@type": "Question",
        name: "What should I wear to my appointment?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Please wear loose, comfortable clothing that allows easy access to the area being treated.",
        },
      },
    ],
  };

  // Track scrolling to toggle visibility of the scroll to top button
  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Seo
        title="Physiotherapy Services | Drax Hall Health Group"
        description="Physiotherapy and rehab care for pain relief, mobility gains, post-surgery recovery, and sports injuries with personalized treatment plans in Drax Hall."
        canonicalPath="/physiotherapy"
        ogImage="/physio-logo.png"
      />
      <StructuredData data={[physioSchema, physioFaq]} />
      <Box sx={{
        backgroundColor: "lavender",
        m: 0
    }}>
      <Navbar />
      <BreadcrumbsNav
        items={[
          { label: "Home", path: "/" },
          { label: "Physiotherapy & Rehab", path: "/physiotherapy" },
        ]}
      />
      <Hero />
      <AboutUs />
      <Services />
      <PatientInfo />
      <ContactUs />
      <Testimonials />
      <Footer />

      {/* Scroll to Top Button */}
      <Zoom in={isVisible}>
        <Fab
          color="secondary"
          size="small"
          onClick={scrollToTop}
          aria-label="scroll back to top"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 100,
            backgroundColor: physioTheme.palette.primary.main,
            '&:hover': {
              backgroundColor: '#a00d56',
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </Box>
    </>
  );
};

export default PhysiotherapyPage;
